import React, { useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import {
  getContracts,
  getContract,
  createContract,
  updateContract,
  deleteContract,
  getPolicyVersions,
  getPolicyVersion,
  restorePolicyVersion,
  getUsers,
  createUser,
  updateUser,
  deleteUser,
  changePassword,
  getRoles,
  getRolePermissions,
  createRole,
  updateRole,
  deleteRole,
  getDepartments,
  getRegions,
  viewFile,
  downloadFile
} from '../services/api';
import ImportModal from './ImportModal';
import { FiUsers, FiFileText, FiAlertTriangle, FiClock, FiPlus, FiEdit2, FiTrash2, FiX, FiKey, FiUpload, FiSearch, FiLock, FiLayers, FiMessageCircle } from 'react-icons/fi';
import AddContractModal from './AddContractModal';
import ChangePasswordModal from './ChangePasswordModal';
import AgentPanel from './AgentPanel';
import gmeLogo from '../assets/gme.png';
import Select from 'react-select';

/** Align with AddContractModal — stable attachment URLs after merge/API quirks */
function normalizeStoredAttachmentRef(raw) {
  if (raw == null || raw === false) return '';
  const s = typeof raw === 'string' ? raw.trim() : String(raw).trim();
  if (!s) return '';
  const norm = s.replace(/\\/g, '/');
  const lower = norm.toLowerCase();
  if (lower.startsWith('http://') || lower.startsWith('https://')) return norm;
  if (lower.includes('/uploads/')) return norm.startsWith('/') ? norm : `/${norm.replace(/^\.+\//, '')}`;
  if (/^\/?uploads\//i.test(norm)) return norm.startsWith('/') ? norm : `/${norm}`;
  return norm;
}

const Dashboard = () => {
  const [contracts, setContracts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [editingContract, setEditingContract] = useState(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [contractToDelete, setContractToDelete] = useState(null);
  const { user, logout, refreshPermissions } = useAuth();
  const navigate = useNavigate();
  const [isUsersModalOpen, setIsUsersModalOpen] = useState(false);
  const [isProfileDropdownOpen, setIsProfileDropdownOpen] = useState(false);
  const [users, setUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [usersError, setUsersError] = useState('');
  const [isCreateUserModalOpen, setIsCreateUserModalOpen] = useState(false);
  const [userForm, setUserForm] = useState({ username: '', email: '', password: '', roleId: '', departmentId: '', regionId: '' });
  const [userModalError, setUserModalError] = useState('');
  const [userModalLoading, setUserModalLoading] = useState(false);
  const [isRolesModalOpen, setIsRolesModalOpen] = useState(false);
  const [rolesTab, setRolesTab] = useState('roles'); // 'roles' or 'permissions'
  const [isChangePasswordModalOpen, setIsChangePasswordModalOpen] = useState(false);
  const [roles, setRoles] = useState([]);
  const [permissions, setPermissions] = useState([]);
  const [loadingRoles, setLoadingRoles] = useState(false);
  const [loadingPermissions, setLoadingPermissions] = useState(false);
  const [rolesError, setRolesError] = useState('');
  const [permissionsError, setPermissionsError] = useState('');
  const [isCreateRoleModalOpen, setIsCreateRoleModalOpen] = useState(false);
  const [createRoleForm, setCreateRoleForm] = useState({ name: '', description: '', permissions: [], regionIds: [], departmentIds: [] });
  const [createRoleError, setCreateRoleError] = useState('');
  const [createRoleLoading, setCreateRoleLoading] = useState(false);
  const [editingRole, setEditingRole] = useState(null);
  const [userRoles, setUserRoles] = useState([]);
  const [editingUser, setEditingUser] = useState(null);
  const [isEditUserModalOpen, setIsEditUserModalOpen] = useState(false);
  const [editUserForm, setEditUserForm] = useState({ username: '', email: '', password: '', roleId: '', departmentId: '', regionId: '' });
  const [editUserError, setEditUserError] = useState('');
  const [editUserLoading, setEditUserLoading] = useState(false);
  const [deleteUserId, setDeleteUserId] = useState(null);
  const [deleteUserLoading, setDeleteUserLoading] = useState(false);
  const [departments, setDepartments] = useState([]);
  const [regions, setRegions] = useState([]);
  const [filterDepartment, setFilterDepartment] = useState(null);
  const [filterVendor, setFilterVendor] = useState(null);
  const [filterStatus, setFilterStatus] = useState({ value: 'Active', label: 'Active' }); // Default to Active
  const [filterExpiry, setFilterExpiry] = useState(null); // 'all', 'active', 'expiring', 'expired'
  const [filterDocumentType, setFilterDocumentType] = useState(null);
  const [filterRegion, setFilterRegion] = useState(null);
  const [isImportModalOpen, setIsImportModalOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [sortConfig, setSortConfig] = useState({ key: 'end_date', direction: 'asc' });
  const [searchQuery, setSearchQuery] = useState('');
  const [versionsModalContract, setVersionsModalContract] = useState(null);
  const [policyVersions, setPolicyVersions] = useState([]);
  const [selectedVersionSnapshot, setSelectedVersionSnapshot] = useState(null);
  const [versionsLoading, setVersionsLoading] = useState(false);
  const [versionsError, setVersionsError] = useState('');
  const [restoreLoading, setRestoreLoading] = useState(false);
  const [isAgentPanelOpen, setIsAgentPanelOpen] = useState(false);
  const SHOW_AGENT = false; // Set to true to show AI Agent panel and button

  // Helper: can current user delete contracts?
  const userPermissions = Array.isArray(user?.permissions) ? user.permissions : [];
  const isAdminLike =
    user &&
    (user.role === 'admin' ||
      user.role_name === 'admin' ||
      user.role === 'superadmin' ||
      user.role_name === 'superadmin');
  const canDeleteContracts =
    isAdminLike ||
    userPermissions.includes('contracts:delete') ||
    userPermissions.includes('licenses:delete') ||
    userPermissions.includes('policies:delete') ||
    userPermissions.includes('civilid:delete');

  const fetchContracts = async () => {
    setLoading(true);
    setError('');

    try {
      console.log('Fetching contracts...');
      const data = await getContracts();
      console.log('Contracts API response:', data);

      // Check if data is an array before setting it
      if (Array.isArray(data)) {
        console.log(`Received ${data.length} contracts`);
        setContracts(data);
      } else {
        console.warn('Expected array but received:', data);
        setContracts([]);
      }

      setError('');
    } catch (err) {
      console.error('Error in fetchContracts:', err);

      let errorMessage = 'Failed to fetch contracts';

      if (err.message) {
        errorMessage = err.message;
      }

      setError(errorMessage);
      setContracts([]); // Reset contracts to prevent showing stale data
    } finally {
      setLoading(false);
    }
  };

  const fetchUsers = async () => {
    setLoadingUsers(true);
    setUsersError('');
    try {
      const data = await getUsers();
      // Ensure data is an array before setting
      if (Array.isArray(data)) {
        setUsers(data);
      } else {
        console.error('Expected array but received:', data);
        setUsersError('Invalid data format received from server');
        // Don't clear existing users if data format is wrong
      }
    } catch (err) {
      console.error('Error fetching users:', err);
      setUsersError(err.message);
      // Don't clear users on error, keep existing list
    } finally {
      setLoadingUsers(false);
    }
  };

  const fetchUserRoles = async () => {
    try {
      const data = await getRoles();
      if (Array.isArray(data)) {
        // Filter roles based on user's role
        const isAdmin = user && (user.role === 'admin' || user.role_name === 'admin');
        const isSuperAdmin = user && (user.role === 'superadmin' || user.role_name === 'superadmin');
        const filteredRoles = data.filter(role => {
          if (isSuperAdmin) {
            // Superadmin can see all roles except manager
            return role.name !== 'manager';
          } else if (isAdmin) {
            // Admins can see all roles except manager and superadmin
            return role.name !== 'manager' && role.name !== 'superadmin';
          } else {
            // Department admins cannot see manager, admin, or superadmin roles
            return role.name !== 'manager' && role.name !== 'admin' && role.name !== 'superadmin';
          }
        });
        setUserRoles(filteredRoles);
      }
    } catch (err) {
      // ignore for now
    }
  };

  const fetchDepartments = async () => {
    try {
      const data = await getDepartments();
      if (Array.isArray(data)) setDepartments(data);
    } catch (err) {
      console.error(err);
    }
  };

  const fetchRegions = async () => {
    try {
      const data = await getRegions();
      if (Array.isArray(data)) setRegions(data);
    } catch (err) {
      console.error('Error fetching regions:', err);
    }
  };


  const handleOpenUsersModal = () => {
    setIsUsersModalOpen(true);
    fetchUsers();
    fetchUserRoles();
    fetchDepartments();
    // Superadmin can manage all regions, so load them
    if (user && (user.role === 'superadmin' || user.role_name === 'superadmin')) {
      fetchRegions();
    }
  };

  const fetchRoles = async () => {
    setLoadingRoles(true);
    setRolesError('');
    try {
      const data = await getRoles();
      setRoles(data);
    } catch (err) {
      setRolesError(err.message);
    } finally {
      setLoadingRoles(false);
    }
  };
  const fetchPermissions = async () => {
    setLoadingPermissions(true);
    setPermissionsError('');
    try {
      const data = await getRolePermissions();
      setPermissions(data);
    } catch (err) {
      setPermissionsError(err.message);
    } finally {
      setLoadingPermissions(false);
    }
  };
  const handleOpenRolesModal = () => {
    setIsRolesModalOpen(true);
    fetchRoles();
    fetchPermissions();
    // Load scope options for role creation/editing
    fetchDepartments();
    fetchRegions();
  };

  // Debug logging
  console.log('Dashboard rendering', {
    user,
    hasToken: !!localStorage.getItem('token'),
    loading
  });

  useEffect(() => {
    if (user) {
      // If departments are present in user object, use them
      if (user.departments && Array.isArray(user.departments) && user.departments.length > 0) {
        setDepartments(user.departments);
      } else {
        // Otherwise, fetch departments
        fetchDepartments();
      }
      fetchContracts();
    } else {
      navigate('/login');
    }
  }, [user, navigate, logout]);

  // Close profile dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (isProfileDropdownOpen && !event.target.closest('.profile-dropdown-container')) {
        setIsProfileDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isProfileDropdownOpen]);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const handleAddContract = async (formData, isEdit = false) => {
    try {
      // For debugging: Log the form data being sent (excluding the file)
      const formDataObj = {};
      formData.forEach((value, key) => {
        if (key === 'file') {
          const f = value;
          formDataObj[key] =
            f && typeof f === 'object' && (typeof f.size === 'number' || f instanceof File)
              ? `File: ${f.name || 'upload'} (${f.size ?? '?'} bytes)`
              : String(value);
        } else {
          formDataObj[key] = value;
        }
      });

      console.log(isEdit ? 'Updating contract data:' : 'Submitting contract data:', formDataObj);

      let response;
      if (isEdit) {
        if (!editingContract || !editingContract.id) {
          throw new Error('No contract ID provided for update');
        }
        response = await updateContract(editingContract.id, formData);
      } else {
        response = await createContract(formData);
      }

      console.log('API Response:', response);

      // Refresh the contracts list
      await fetchContracts();
      return response;
    } catch (error) {
      console.error('Error adding contract:', error);
      throw error;
    }
  };

  // Format date consistently as DD/MM/YYYY
  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return '';
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  };

  // Format currency with appropriate symbol
  const formatCurrency = (value, currency = 'USD') => {
    if (!value) return '';
    const currencySymbols = {
      'USD': '$',
      'EUR': '€',
      'GBP': '£',
      'JPY': '¥',
      'AUD': 'A$',
      'CAD': 'C$',
      'CHF': 'Fr',
      'CNY': '¥',
      'INR': '₹',
      'KD': 'KD',
      'AED': 'AED',
      'SAR': 'SAR',
      'QAR': 'QAR',
      'OMR': 'OMR'
    };
    const symbol = currencySymbols[currency] || currency;
    // For JPY, typically no decimal places
    if (currency === 'JPY') {
      return `${symbol} ${Math.round(value).toLocaleString()}`;
    }
    return `${symbol} ${value.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const getDaysRemaining = (endDate) => {
    const today = new Date();
    const end = new Date(endDate);
    const diffTime = end - today;
    return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  };

  // Cap for "Expiring Soon" badge: show only when within this many days (notification window can be 365)
  const EXPIRING_SOON_BADGE_DAYS = 180;

  // Get the maximum alert day from a contract's alert_days field (used for notification schedule)
  const getMaxAlertDay = (contract) => {
    // Try both snake_case and camelCase field names
    const alertDaysValue = contract?.alert_days || contract?.alertDays;

    if (!contract || (!alertDaysValue && alertDaysValue !== 0)) return 30; // Default to 30 if not set

    // Handle both string and number formats
    let alertDays = [];
    if (typeof alertDaysValue === 'string') {
      alertDays = alertDaysValue
        .split(',')
        .map(s => parseInt(s.trim(), 10))
        .filter(n => !isNaN(n) && n > 0);
    } else if (typeof alertDaysValue === 'number') {
      alertDays = [alertDaysValue];
    } else if (Array.isArray(alertDaysValue)) {
      alertDays = alertDaysValue
        .map(v => typeof v === 'string' ? parseInt(v.trim(), 10) : v)
        .filter(n => !isNaN(n) && n > 0);
    }

    const maxDay = alertDays.length > 0 ? Math.max(...alertDays) : 30;

    return maxDay;
  };

  // Days threshold for showing "Expiring Soon" badge (capped so badge only in a short window)
  const getExpiringSoonBadgeDays = (contract) => {
    return Math.min(getMaxAlertDay(contract), EXPIRING_SOON_BADGE_DAYS);
  };

  const getStatusColor = (days, badgeDays = 30) => {
    if (days <= 0) return 'bg-red-100 text-red-800';
    if (days <= badgeDays) return 'bg-yellow-100 text-yellow-800';
    return 'bg-green-100 text-green-800';
  };

  const handleEditContract = async (contract) => {
    if (!contract?.id) return;

    const pickAttachmentPath = (row) => {
      if (!row || typeof row !== 'object' || Array.isArray(row)) return '';
      const directVals = [row.file_url, row.fileUrl, row.FILE_URL, row.fileURL];
      for (const raw of directVals) {
        if (raw == null || raw === false) continue;
        const s = typeof raw === 'string' ? raw.trim() : String(raw).trim();
        if (s) return normalizeStoredAttachmentRef(s);
      }
      const dynamicKeys = Object.keys(row).filter((x) => /file/i.test(x) && /url/i.test(x));
      dynamicKeys.sort((a, b) => {
        const pri = (x) => (/^file_url$/i.test(x) ? 0 : /^fileUrl$/i.test(x) ? 1 : 2);
        return pri(a) - pri(b) || a.localeCompare(b);
      });
      for (const dk of dynamicKeys) {
        const val = row[dk];
        if (val == null || val === false) continue;
        const s = String(val).trim();
        if (s) return normalizeStoredAttachmentRef(s);
      }
      for (const [, val] of Object.entries(row)) {
        if (val == null || val === false) continue;
        const s = typeof val === 'string' ? val.trim() : String(val).trim();
        if (!s) continue;
        const norm = s.replace(/\\/g, '/').toLowerCase();
        if (norm.includes('/uploads/') || /^\.?\/?uploads\//i.test(norm)) {
          return normalizeStoredAttachmentRef(s);
        }
      }
      return '';
    };

    const pickEmployeeVendor = (row) => {
      if (!row || typeof row !== 'object' || Array.isArray(row)) return '';
      const v = row.vendor ?? row.VENDOR ?? row.employee_name ?? row.employeeName;
      if (v != null && String(v).trim() !== '') return String(v).trim();
      return '';
    };

    try {
      const full = await getContract(contract.id);
      console.log('[handleEditContract] list row file_url:', contract?.file_url, '| detail file_url:', full?.file_url, '| type:', contract?.type);
      if (!full || typeof full !== 'object' || Array.isArray(full)) {
        setEditingContract(contract);
      } else {
        const merged = { ...contract, ...full };
        // Prefer list row path first: spread detail overwrites file_url with null/undefined from drivers
        const path =
          pickAttachmentPath(contract) ||
          pickAttachmentPath(full) ||
          pickAttachmentPath(merged);
        console.log('[handleEditContract] resolved attachment path:', path, '| contract.id:', contract.id);
        if (path) {
          merged.file_url = path;
          merged.fileUrl = path;
        }
        const empVendor =
          pickEmployeeVendor(merged) ||
          pickEmployeeVendor(full) ||
          pickEmployeeVendor(contract);
        if (empVendor) {
          merged.vendor = empVendor;
        }
        setEditingContract(merged);
      }
    } catch (err) {
      console.error('Failed to load contract for edit:', err);
      setEditingContract(contract);
    }
    setIsAddModalOpen(true);
  };

  const handleDeleteClick = (contract) => {
    setContractToDelete(contract);
    setIsDeleteModalOpen(true);
  };

  const openVersionsModal = async (contract) => {
    if (!contract || (contract.type || '').trim() !== 'Policy') return;
    setVersionsModalContract(contract);
    setPolicyVersions([]);
    setSelectedVersionSnapshot(null);
    setVersionsError('');
    setVersionsLoading(true);
    try {
      const list = await getPolicyVersions(contract.id);
      setPolicyVersions(Array.isArray(list) ? list : []);
    } catch (err) {
      setVersionsError(err.message || 'Failed to load version history');
      setPolicyVersions([]);
    } finally {
      setVersionsLoading(false);
    }
  };

  const viewVersionSnapshot = async (versionNumber) => {
    if (!versionsModalContract) return;
    setVersionsLoading(true);
    setVersionsError('');
    try {
      const snap = await getPolicyVersion(versionsModalContract.id, versionNumber);
      setSelectedVersionSnapshot(snap);
    } catch (err) {
      setVersionsError(err.message || 'Failed to load version');
      setSelectedVersionSnapshot(null);
    } finally {
      setVersionsLoading(false);
    }
  };

  const handleRestoreVersion = async (versionNumber) => {
    if (!versionsModalContract || !window.confirm(`Restore this policy to version ${versionNumber}? The current content will be replaced.`)) return;
    setRestoreLoading(true);
    setVersionsError('');
    try {
      await restorePolicyVersion(versionsModalContract.id, versionNumber);
      await fetchContracts();
      setVersionsModalContract(null);
      setSelectedVersionSnapshot(null);
      setPolicyVersions([]);
    } catch (err) {
      setVersionsError(err.message || 'Failed to restore version');
    } finally {
      setRestoreLoading(false);
    }
  };

  const handleDeleteContract = async () => {
    if (!contractToDelete) return;

    try {
      await deleteContract(contractToDelete.id);

      // Refresh the contracts list
      await fetchContracts();
      setIsDeleteModalOpen(false);
      setContractToDelete(null);
    } catch (error) {
      console.error('Error deleting contract:', error);
      setError('Failed to delete contract');
    }
  };


  const handleDownloadFile = (fileUrl) => {
    if (!fileUrl) return;
    downloadFile(fileUrl);
  };


  // Calculate contract status counts
  const getContractStats = () => {
    const today = new Date();
    const stats = {
      active: 0,
      expiring: 0,
      expired: 0,
      total: contracts.length
    };

    contracts.forEach(contract => {
      const endDate = new Date(contract.end_date);
      const daysRemaining = Math.ceil((endDate - today) / (1000 * 60 * 60 * 24));
      const badgeDays = getExpiringSoonBadgeDays(contract);

      if (daysRemaining <= 0) {
        stats.expired++;
      } else if (daysRemaining <= badgeDays) {
        stats.expiring++;
      } else {
        stats.active++;
      }
    });

    return stats;
  };

  const contractStats = getContractStats();

  const openEditRoleModal = (role) => {
    setEditingRole(role);
    setCreateRoleForm({
      name: role.name || '',
      description: role.description || '',
      permissions: role.permissions || [],
      // Coerce IDs to numbers so multi-select <option value={id}> matches
      regionIds: (role.region_ids || []).map(id => Number(id)).filter(n => !Number.isNaN(n)),
      departmentIds: (role.department_ids || []).map(id => Number(id)).filter(n => !Number.isNaN(n)),
    });
    setIsCreateRoleModalOpen(true);
  };

  const handleCreateRole = async (e) => {
    e.preventDefault();
    setCreateRoleLoading(true);
    setCreateRoleError('');

    try {
      let response;
      if (editingRole) {
        response = await updateRole(editingRole.id, createRoleForm);
      } else {
        response = await createRole(createRoleForm);
      }

      // Reset form and state
      setIsCreateRoleModalOpen(false);
      setCreateRoleForm({ name: '', description: '', permissions: [], regionIds: [], departmentIds: [] });
      setEditingRole(null);
      fetchRoles();
    } catch (err) {
      setCreateRoleError(err.message);
    } finally {
      setCreateRoleLoading(false);
    }
  };


  const handleCreateUser = async (e) => {
    e.preventDefault();
    setUserModalLoading(true);
    setUserModalError('');

    try {
      const payload = { ...userForm };

      // Default role if not selected
      if (!payload.roleId && userRoles.length > 0) payload.roleId = userRoles[0].id;

      // Only send departmentId if user is admin/superadmin (department admins don't send it - backend uses their department)
      const isAdmin = user && (user.role === 'admin' || user.role_name === 'admin' || user.role === 'superadmin' || user.role_name === 'superadmin');
      if (!isAdmin) {
        delete payload.departmentId; // Backend will use creator's department automatically
      } else if (!payload.departmentId && departments.length > 0) {
        payload.departmentId = departments[0].id;
      }

      // Only superadmin can set region explicitly; others are forced to their own region on the backend
      if (!(user && (user.role === 'superadmin' || user.role_name === 'superadmin'))) {
        delete payload.regionId;
      }

      await createUser(payload);

      // Reset form and state
      setIsCreateUserModalOpen(false);
      setUserForm({ username: '', email: '', password: '', roleId: '', departmentId: '', regionId: '' });

      // Wait for users to be fetched before closing modal
      await fetchUsers();
    } catch (err) {
      setUserModalError(err.message);
      console.error('Error creating user:', err);
    } finally {
      setUserModalLoading(false);
    }
  };

  const openEditUserModal = (userRecord) => {
    setEditingUser(userRecord);
    setEditUserForm({
      username: userRecord.username || '',
      email: userRecord.email || '',
      password: '',
      roleId: userRecord.role_id || userRecord.roleId || '',
      departmentId: userRecord.department_id || userRecord.departmentId || '',
      regionId: userRecord.region_id || userRecord.regionId || '',
    });
    setIsEditUserModalOpen(true);
  };

  const handleEditUser = async (e) => {
    e.preventDefault();
    setEditUserLoading(true);
    setEditUserError('');

    try {
      const payload = { ...editUserForm };

      // Default role if not selected
      if (!payload.roleId && userRoles.length > 0) payload.roleId = userRoles[0].id;

      // Only send departmentId if user is admin/superadmin (department admins don't send it - backend keeps existing)
      const isAdmin = user && (user.role === 'admin' || user.role_name === 'admin' || user.role === 'superadmin' || user.role_name === 'superadmin');
      if (!isAdmin) {
        delete payload.departmentId; // Backend will keep existing department
      } else if (!payload.departmentId && departments.length > 0) {
        payload.departmentId = departments[0].id;
      }

      // Only superadmin can change region explicitly
      if (!(user && (user.role === 'superadmin' || user.role_name === 'superadmin'))) {
        delete payload.regionId;
      }

      // Build update payload - only include departmentId for admin/superadmin
      const updatePayload = {
        username: payload.username,
        email: payload.email,
        roleId: payload.roleId,
      };

      // Only include departmentId if user is admin/superadmin
      if (isAdmin && payload.departmentId) {
        updatePayload.departmentId = payload.departmentId;
      }

      // Only include regionId if user is superadmin
      if (payload.regionId) {
        updatePayload.regionId = payload.regionId;
      }

      // Update user details
      await updateUser(editingUser.id, updatePayload);

      // If password was provided, update password via dedicated endpoint (hashed server-side)
      if (payload.password && String(payload.password).trim() !== '') {
        await changePassword(editingUser.id, '', payload.password);
      }

      setIsEditUserModalOpen(false);
      setEditingUser(null);
      fetchUsers();
    } catch (err) {
      setEditUserError(err.message);
    } finally {
      setEditUserLoading(false);
    }
  };

  const handleDeleteUser = async () => {
    if (!deleteUserId) return;
    setDeleteUserLoading(true);

    try {
      await deleteUser(deleteUserId);
      setDeleteUserId(null);
      fetchUsers();
    } catch (err) {
      console.error('Error deleting user:', err);
    } finally {
      setDeleteUserLoading(false);
    }
  };

  // Unique vendors for filter
  const vendorOptions = Array.from(new Set(contracts.map(c => c.vendor).filter(Boolean))).map(v => ({ value: v, label: v }));
  const departmentOptions = departments.map(d => ({ value: d.id, label: d.name }));
  const statusOptions = [
    { value: 'Active', label: 'Active' },
    { value: 'Inactive', label: 'Inactive' },
  ];
  const expiryOptions = [
    { value: 'all', label: 'All' },
    { value: 'active', label: 'Active' },
    { value: 'expiring', label: 'Expiring Soon' },
    { value: 'expired', label: 'Expired' },
  ];
  const documentTypeOptions = [
    { value: 'Contract', label: 'Contract' },
    { value: 'License', label: 'License' },
    { value: 'Policy', label: 'Policy' },
    { value: 'Civil ID', label: 'Civil ID' },
    { value: 'OHC', label: 'OHC' },
    { value: 'Passport', label: 'Passport' },
    { value: 'Residence Visa', label: 'Residence Visa' },
    { value: 'Work Permit', label: 'Work Permit' },
  ];
  const regionOptions = [
    { value: 'Kuwait', label: 'Kuwait' },
    { value: 'Dubai', label: 'Dubai' },
    { value: 'KSA', label: 'KSA' },
    { value: 'Bahrain', label: 'Bahrain' },
    { value: 'Oman', label: 'Oman' },
    { value: 'Qatar', label: 'Qatar' },
  ];

  // Filter contracts
  const filteredContracts = contracts.filter(contract => {
    let pass = true;

    // Search filter - search across name, vendor, description, department, and notification email
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      const matchesName = contract.name?.toLowerCase().includes(query);
      const matchesVendor = contract.vendor?.toLowerCase().includes(query);
      const matchesDescription = contract.description?.toLowerCase().includes(query);
      const matchesDepartment = contract.department_name?.toLowerCase().includes(query);
      const matchesEmail = contract.notification_email?.toLowerCase().includes(query);

      if (!matchesName && !matchesVendor && !matchesDescription && !matchesDepartment && !matchesEmail) {
        pass = false;
      }
    }

    if (filterDepartment && contract.department_id !== filterDepartment.value && contract.departmentId !== filterDepartment.value) pass = false;
    if (filterVendor && contract.vendor !== filterVendor.value) pass = false;
    // Filter by status - if filterStatus is set, only show contracts matching that status
    if (filterStatus && contract.status !== filterStatus.value) pass = false;
    // Filter by document type
    if (filterDocumentType && contract.type !== filterDocumentType.value) pass = false;
    // Filter by region
    if (filterRegion && contract.region !== filterRegion.value) pass = false;
    if (filterExpiry && filterExpiry.value !== 'all') {
      const days = getDaysRemaining(contract.end_date);
      const badgeDays = getExpiringSoonBadgeDays(contract);
      if (filterExpiry.value === 'active' && days > badgeDays) return true;
      if (filterExpiry.value === 'expiring' && days > 0 && days <= badgeDays) return true;
      if (filterExpiry.value === 'expired' && days <= 0) return true;
      return false;
    }
    return pass;
  });

  // Sorting
  const sortedContracts = React.useMemo(() => {
    const sortable = [...filteredContracts];
    if (!sortConfig || !sortConfig.key || !sortConfig.direction) return sortable;

    const getValue = (contract, key) => {
      switch (key) {
        case 'start_date':
          return contract.start_date ? new Date(contract.start_date) : null;
        case 'end_date':
          return contract.end_date ? new Date(contract.end_date) : null;
        case 'value':
          return contract.value != null ? Number(contract.value) : 0;
        default:
          return (contract[key] || '').toString().toLowerCase();
      }
    };

    sortable.sort((a, b) => {
      const aVal = getValue(a, sortConfig.key);
      const bVal = getValue(b, sortConfig.key);

      if (aVal === null && bVal === null) return 0;
      if (aVal === null) return 1;
      if (bVal === null) return -1;

      if (aVal < bVal) return sortConfig.direction === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });

    return sortable;
  }, [filteredContracts, sortConfig]);

  const handleSort = (key) => {
    setSortConfig((prev) => {
      if (prev.key === key) {
        // Toggle between asc, desc, and off
        if (prev.direction === 'asc') return { key, direction: 'desc' };
        if (prev.direction === 'desc') return { key: null, direction: null };
      }
      return { key, direction: 'asc' };
    });
  };

  const getSortIndicator = (key) => {
    if (sortConfig.key !== key || !sortConfig.direction) return '';
    return sortConfig.direction === 'asc' ? '▲' : '▼';
  };

  // Pagination logic
  const totalPages = Math.ceil(sortedContracts.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedContracts = sortedContracts.slice(startIndex, endIndex);

  // Reset to page 1 when filters or search change
  useEffect(() => {
    setCurrentPage(1);
  }, [filterDepartment, filterVendor, filterStatus, filterExpiry, filterDocumentType, filterRegion, searchQuery]);

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  const handleOpenAddContractModal = async () => {
    if (user && (user.role === 'admin' || user.role_name === 'admin')) {
      // Fetch departments if not already loaded
      if (!departments || departments.length === 0) {
        await fetchDepartments();
      }
    }
    setEditingContract(null);
    setIsAddModalOpen(true);
  };

  const handleImportSuccess = () => {
    // Refresh contracts list after successful import
    fetchContracts();
  };

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Header with user profile */}
      <header className="flex items-center justify-between px-3 sm:px-6 py-3 sm:py-4 bg-white shadow-md border-b border-gray-200">
        <div className="flex items-center space-x-2 sm:space-x-4 min-w-0 flex-1">
          <h1 className="text-sm sm:text-base font-bold text-purple-700 flex items-center space-x-1 sm:space-x-2 truncate">
            <img src={gmeLogo} alt="Gastronomica Logo" className="h-8 sm:h-10 w-auto mr-1 sm:mr-2 flex-shrink-0" />
            <span className="hidden xs:inline">DocumentHub</span>
            <span className="xs:hidden">DocumentHub</span>
          </h1>
        </div>
        <div className="flex items-center space-x-2 sm:space-x-4 flex-shrink-0">
          {/* User profile section */}
          {user && (
            <div className="relative group profile-dropdown-container">
              <button
                className="flex items-center space-x-2 focus:outline-none"
                onClick={() => setIsProfileDropdownOpen(!isProfileDropdownOpen)}
              >
                <span className="inline-block h-10 w-10 rounded-full bg-purple-200 text-purple-700 flex items-center justify-center font-bold text-lg">
                  {user.username ? user.username.charAt(0).toUpperCase() : user.email.charAt(0).toUpperCase()}
                </span>
                <span className="hidden sm:flex flex-col items-start">
                  <span className="font-semibold text-gray-800">{user.username || user.email}</span>
                  <span className="text-xs text-gray-500">{user.email}</span>
                </span>
                <svg className={`w-4 h-4 ml-1 text-gray-400 group-hover:text-purple-600 transition-transform ${isProfileDropdownOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" /></svg>
              </button>
              <div className={`absolute right-0 mt-2 w-56 bg-white border border-gray-200 rounded shadow-lg transition-opacity z-50 ${isProfileDropdownOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}>
                {/* User Management Option */}
                {user && user.permissions && user.permissions.includes('users:read') && (
                  <button
                    onClick={() => {
                      handleOpenUsersModal();
                      setIsProfileDropdownOpen(false);
                    }}
                    className="w-full text-left px-4 py-2 text-gray-700 hover:bg-blue-50 flex items-center transition-colors"
                  >
                    <FiUsers className="mr-2" />
                    Manage Users
                  </button>
                )}

                {/* Roles & Permissions Option - Show to admins, superadmins, or users with users:read permission */}
                {user && ((user.role === 'admin' || user.role_name === 'admin' || user.role === 'superadmin' || user.role_name === 'superadmin') || (user.permissions && user.permissions.includes('users:read'))) && (
                  <button
                    onClick={() => {
                      handleOpenRolesModal();
                      setIsProfileDropdownOpen(false);
                    }}
                    className="w-full text-left px-4 py-2 text-gray-700 hover:bg-purple-50 flex items-center transition-colors"
                  >
                    <FiKey className="mr-2" />
                    Roles & Permissions
                  </button>
                )}

                {SHOW_AGENT && (
                  <>
                    {/* AI Agent */}
                    <button
                      onClick={() => {
                        setIsAgentPanelOpen(true);
                        setIsProfileDropdownOpen(false);
                      }}
                      className="w-full text-left px-4 py-2 text-gray-700 hover:bg-indigo-50 flex items-center transition-colors"
                    >
                      <FiMessageCircle className="mr-2" />
                      AI Agent
                    </button>
                  </>
                )}

                {/* Change Password Option */}
                <button
                  onClick={() => {
                    setIsChangePasswordModalOpen(true);
                    setIsProfileDropdownOpen(false);
                  }}
                  className="w-full text-left px-4 py-2 text-gray-700 hover:bg-green-50 flex items-center transition-colors"
                >
                  <FiLock className="mr-2" />
                  Change Password
                </button>

                {/* Departments & Regions Options - For admin and superadmin users */}
                {user && (user.role === 'admin' || user.role_name === 'admin' || user.role === 'superadmin' || user.role_name === 'superadmin') && (
                  <>
                    <button
                      onClick={() => {
                        navigate('/departments');
                        setIsProfileDropdownOpen(false);
                      }}
                      className="w-full text-left px-4 py-2 text-gray-700 hover:bg-green-50 flex items-center transition-colors"
                    >
                      <FiUsers className="mr-2" />
                      Manage Departments
                    </button>
                    <button
                      onClick={() => {
                        navigate('/regions');
                        setIsProfileDropdownOpen(false);
                      }}
                      className="w-full text-left px-4 py-2 text-gray-700 hover:bg-green-50 flex items-center transition-colors"
                    >
                      <FiUsers className="mr-2" />
                      Manage Regions
                    </button>
                  </>
                )}

                {/* Divider */}
                <div className="border-t border-gray-100"></div>

                {/* Logout Option */}
                <button
                  onClick={() => {
                    logout();
                    setIsProfileDropdownOpen(false);
                  }}
                  className="w-full text-left px-4 py-2 text-gray-700 hover:bg-red-50 flex items-center transition-colors"
                >
                  <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                  </svg>
                  Logout
                </button>
              </div>
            </div>
          )}
        </div>
      </header>
      {/* Main Content */}
      <main className="max-w-7xl mx-auto py-4 sm:py-6 px-3 sm:px-4 lg:px-8">
        {/* Page Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 sm:mb-8 gap-4">
          <h2 className="text-xl sm:text-2xl font-bold text-gray-900">Document Overview</h2>
          <div className="flex flex-col sm:flex-row gap-2 sm:gap-3 w-full sm:w-auto">
            {user && user.permissions && user.permissions.includes('contracts:create') && (
              <>
                <button
                  className="inline-flex items-center justify-center px-3 sm:px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 text-sm sm:text-base"
                  onClick={() => setIsImportModalOpen(true)}
                >
                  <FiUpload className="mr-2" />
                  <span className="hidden sm:inline">Import from Excel</span>
                  <span className="sm:hidden">Import</span>
                </button>
                <button
                  className="inline-flex items-center justify-center px-3 sm:px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 text-sm sm:text-base"
                  onClick={handleOpenAddContractModal}
                >
                  <FiPlus className="mr-2" />
                  <span className="hidden sm:inline">Add New Doc</span>
                  <span className="sm:hidden">Add Doc</span>
                </button>
              </>
            )}
          </div>
        </div>

        {/* Status Cards */}
        <div className="grid grid-cols-1 gap-4 sm:gap-6 sm:grid-cols-2 lg:grid-cols-4 mb-6 sm:mb-8">
          {/* Total Contracts Card */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 hover:shadow-md transition-shadow duration-200 p-4 sm:p-6">
            <div className="flex items-center space-x-4">
              <div className="bg-blue-500 rounded-lg p-3">
                <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                </svg>
              </div>
              <div>
                <p className="text-sm font-medium text-gray-500">Total Documents</p>
                <p className="text-2xl font-bold text-gray-900">{contractStats.total}</p>
              </div>
            </div>
          </div>

          {/* Active Contracts Card */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 hover:shadow-md transition-shadow duration-200 p-4 sm:p-6">
            <div className="flex items-center space-x-4">
              <div className="bg-green-500 rounded-lg p-3">
                <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <div>
                <p className="text-sm font-medium text-gray-500">Active</p>
                <p className="text-2xl font-bold text-gray-900">{contractStats.active}</p>
              </div>
            </div>
          </div>

          {/* Expiring Soon Card */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 hover:shadow-md transition-shadow duration-200 p-4 sm:p-6">
            <div className="flex items-center space-x-4">
              <div className="bg-yellow-500 rounded-lg p-3">
                <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <div>
                <p className="text-sm font-medium text-gray-500">Expiring Soon</p>
                <p className="text-2xl font-bold text-gray-900">{contractStats.expiring}</p>
              </div>
            </div>
          </div>

          {/* Expired Contracts Card */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 hover:shadow-md transition-shadow duration-200 p-4 sm:p-6">
            <div className="flex items-center space-x-4">
              <div className="bg-red-500 rounded-lg p-3">
                <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </div>
              <div>
                <p className="text-sm font-medium text-gray-500">Expired</p>
                <p className="text-2xl font-bold text-gray-900">{contractStats.expired}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Filter Bar */}
        <div className="bg-white shadow-md rounded-xl px-4 sm:px-6 lg:px-8 py-4 sm:py-6 mb-6 sm:mb-8 flex flex-col gap-4 sm:gap-6 border border-gray-100 relative z-20">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-7 gap-4 w-full items-end">
            {user && (user.role === 'admin' || user.role_name === 'admin') && (
              <div className="flex flex-col w-full">
                <label className="text-xs font-semibold text-gray-600 mb-1 flex items-center gap-1"><FiUsers className="inline-block text-purple-500" /> Department</label>
                <Select
                  options={departmentOptions}
                  value={filterDepartment}
                  onChange={setFilterDepartment}
                  isClearable
                  placeholder="All"
                  classNamePrefix="react-select"
                  menuPortalTarget={document.body}
                  styles={{
                    menuPortal: (base) => ({ ...base, zIndex: 9999 })
                  }}
                />
              </div>
            )}
            <div className="flex flex-col w-full">
              <label className="text-xs font-semibold text-gray-600 mb-1 flex items-center gap-1"><FiFileText className="inline-block text-blue-500" /> Vendor</label>
              <Select
                options={vendorOptions}
                value={filterVendor}
                onChange={setFilterVendor}
                isClearable
                placeholder="All"
                classNamePrefix="react-select"
                menuPortalTarget={document.body}
                styles={{
                  menuPortal: (base) => ({ ...base, zIndex: 9999 })
                }}
              />
            </div>
            <div className="flex flex-col w-full">
              <label className="text-xs font-semibold text-gray-600 mb-1 flex items-center gap-1"><FiAlertTriangle className="inline-block text-yellow-500" /> Status</label>
              <Select
                options={statusOptions}
                value={filterStatus}
                onChange={(value) => setFilterStatus(value || { value: 'Active', label: 'Active' })}
                isClearable={false}
                placeholder="All Statuses"
                classNamePrefix="react-select"
                menuPortalTarget={document.body}
                styles={{
                  menuPortal: (base) => ({ ...base, zIndex: 9999 })
                }}
              />
            </div>
            <div className="flex flex-col w-full">
              <label className="text-xs font-semibold text-gray-600 mb-1 flex items-center gap-1"><FiClock className="inline-block text-red-500" /> Expiry</label>
              <Select
                options={expiryOptions}
                value={filterExpiry}
                onChange={setFilterExpiry}
                isClearable={false}
                placeholder="All"
                defaultValue={expiryOptions[0]}
                classNamePrefix="react-select"
                menuPortalTarget={document.body}
                styles={{
                  menuPortal: (base) => ({ ...base, zIndex: 9999 })
                }}
              />
            </div>
            <div className="flex flex-col w-full">
              <label className="text-xs font-semibold text-gray-600 mb-1 flex items-center gap-1"><FiFileText className="inline-block text-indigo-500" /> Doc Type</label>
              <Select
                options={documentTypeOptions}
                value={filterDocumentType}
                onChange={setFilterDocumentType}
                isClearable
                placeholder="All"
                classNamePrefix="react-select"
                menuPortalTarget={document.body}
                styles={{
                  menuPortal: (base) => ({ ...base, zIndex: 9999 })
                }}
              />
            </div>
            <div className="flex flex-col w-full">
              <label className="text-xs font-semibold text-gray-600 mb-1 flex items-center gap-1"><svg className="inline-block text-green-500" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z" /></svg> Region</label>
              <Select
                options={regionOptions}
                value={filterRegion}
                onChange={setFilterRegion}
                isClearable
                placeholder="All"
                classNamePrefix="react-select"
                menuPortalTarget={document.body}
                styles={{
                  menuPortal: (base) => ({ ...base, zIndex: 9999 })
                }}
              />
            </div>
            <div className="flex flex-col w-full">
              <label className="text-xs font-semibold text-gray-600 mb-1 opacity-0">Clear</label>
              <button
                className="w-full inline-flex items-center justify-center gap-1.5 px-3 sm:px-4 py-2 bg-white text-gray-700 font-medium rounded-md border border-gray-300 shadow-sm hover:bg-gray-50 transition-colors duration-150 focus:outline-none focus:ring-2 focus:ring-blue-400 focus:ring-offset-2 text-xs sm:text-sm h-[38px]"
                onClick={() => {
                  setFilterDepartment(null);
                  setFilterVendor(null);
                  setFilterStatus({ value: 'Active', label: 'Active' });
                  setFilterExpiry(null);
                  setFilterDocumentType(null);
                  setFilterRegion(null);
                }}
                title="Clear all filters"
              >
                <FiX className="text-sm sm:text-base" />
                <span>Clear</span>
              </button>
            </div>
          </div>
        </div>

        {/* Contracts List */}
        <div className="bg-white shadow overflow-hidden sm:rounded-lg">
          <div className="px-6 py-4 border-b border-gray-200 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <h3 className="text-lg font-medium text-gray-900">All Docs</h3>
            {/* Search Input */}
            <div className="relative flex-1 sm:max-w-md">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <FiSearch className="h-5 w-5 text-gray-400" />
              </div>
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search contracts by name, vendor..."
                className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-blue-500 focus:border-blue-500 text-sm"
              />
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery('')}
                  className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600"
                  title="Clear search"
                >
                  <FiX className="h-5 w-5" />
                </button>
              )}
            </div>
          </div>

          {error && (
            <div className="px-6 py-4 bg-red-50">
              <p className="text-sm text-red-700">{error}</p>
            </div>
          )}

          <div
            className="overflow-x-auto border border-gray-200/60 rounded-2xl shadow-xl bg-white max-h-[60vh] overflow-y-auto hide-scrollbar backdrop-blur-sm"
            style={{
              scrollbarWidth: 'none',  /* Firefox */
              msOverflowStyle: 'none',  /* IE and Edge */
            }}
          >
            {paginatedContracts.length > 0 ? (
              <table className="min-w-full divide-y divide-gray-100">
                <thead className="bg-gradient-to-r from-indigo-50 via-blue-50 to-indigo-50 sticky top-0 z-20 shadow-lg border-b-2 border-indigo-200/50">
                  <tr>
                    <th
                      className="px-6 py-4 text-left text-xs font-semibold text-indigo-900 uppercase tracking-wider cursor-pointer hover:bg-indigo-100/70 transition-all duration-300 whitespace-nowrap"
                      onClick={() => handleSort('name')}
                    >
                      <div className="flex items-center gap-2.5">
                        <span className="text-indigo-800">Name</span>
                        <span className="text-[0.65rem] font-normal text-indigo-600">{getSortIndicator('name')}</span>
                      </div>
                    </th>
                    <th
                      className="px-6 py-4 text-left text-xs font-semibold text-indigo-900 uppercase tracking-wider cursor-pointer hover:bg-indigo-100/70 transition-all duration-300 whitespace-nowrap"
                      onClick={() => handleSort('vendor')}
                    >
                      <div className="flex items-center gap-2.5">
                        <span className="text-indigo-800">Vendor</span>
                        <span className="text-[0.65rem] font-normal text-indigo-600">{getSortIndicator('vendor')}</span>
                      </div>
                    </th>
                    <th
                      className="px-6 py-4 text-left text-xs font-semibold text-indigo-900 uppercase tracking-wider cursor-pointer hover:bg-indigo-100/70 transition-all duration-300 whitespace-nowrap"
                      onClick={() => handleSort('value')}
                    >
                      <div className="flex items-center gap-2.5">
                        <span className="text-indigo-800">Value</span>
                        <span className="text-[0.65rem] font-normal text-indigo-600">{getSortIndicator('value')}</span>
                      </div>
                    </th>
                    <th
                      className="px-6 py-4 text-left text-xs font-semibold text-indigo-900 uppercase tracking-wider cursor-pointer hover:bg-indigo-100/70 transition-all duration-300 whitespace-nowrap"
                      onClick={() => handleSort('start_date')}
                    >
                      <div className="flex items-center gap-2.5">
                        <span className="text-indigo-800">Start Date</span>
                        <span className="text-[0.65rem] font-normal text-indigo-600">{getSortIndicator('start_date')}</span>
                      </div>
                    </th>
                    <th
                      className="px-6 py-4 text-left text-xs font-semibold text-indigo-900 uppercase tracking-wider cursor-pointer hover:bg-indigo-100/70 transition-all duration-300 whitespace-nowrap"
                      onClick={() => handleSort('end_date')}
                    >
                      <div className="flex items-center gap-2.5">
                        <span className="text-indigo-800">End Date</span>
                        <span className="text-[0.65rem] font-normal text-indigo-600">{getSortIndicator('end_date')}</span>
                      </div>
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-indigo-900 uppercase tracking-wider whitespace-nowrap">
                      <span className="text-indigo-800">Status</span>
                    </th>
                    <th className="px-6 py-4 text-right text-xs font-semibold text-indigo-900 uppercase tracking-wider whitespace-nowrap">
                      <span className="text-indigo-800">Actions</span>
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-100/50">
                  {paginatedContracts.map((contract, idx) => {
                    const daysRemaining = getDaysRemaining(contract.end_date);
                    const badgeDays = getExpiringSoonBadgeDays(contract);
                    const isExpiringSoon = daysRemaining > 0 && daysRemaining <= badgeDays;
                    const isExpired = daysRemaining <= 0;
                    const statusColor = getStatusColor(daysRemaining, badgeDays);
                    const isEven = idx % 2 === 0;

                    // Determine badge classes explicitly
                    let badgeClasses = 'inline-flex items-center px-3 py-1.5 rounded-lg text-xs font-semibold shadow-sm ';
                    if (isExpired) {
                      badgeClasses += 'bg-red-50 text-red-700 border border-red-200';
                    } else if (isExpiringSoon) {
                      // Use orange for expiring soon (more visible than amber/yellow)
                      badgeClasses += 'bg-orange-50 text-orange-700 border border-orange-200';
                    } else {
                      badgeClasses += 'bg-green-50 text-green-700 border border-green-200';
                    }

                    // Debug logging for specific contract
                    if (contract.name === 'AL FARKAD' || (daysRemaining > 0 && daysRemaining <= 100)) {
                      console.log('Badge Debug for', contract.name, {
                        daysRemaining,
                        badgeDays,
                        isExpired,
                        isExpiringSoon,
                        badgeClasses,
                        alert_days: contract.alert_days
                      });
                    }

                    return (
                      <tr
                        key={contract.id}
                        className={`group transition-all duration-300 ease-in-out ${isEven ? 'bg-white' : 'bg-gray-50/40'
                          } hover:bg-gradient-to-r hover:from-blue-50/80 hover:to-indigo-50/80 hover:shadow-md hover:scale-[1.01] border-b border-gray-100/60`}
                      >
                        <td className="px-6 py-5 align-middle">
                          <div className="flex items-center gap-3.5">
                            <div className="flex-shrink-0 w-11 h-11 rounded-xl bg-gradient-to-br from-indigo-500 via-blue-500 to-indigo-600 flex items-center justify-center shadow-md group-hover:shadow-lg group-hover:scale-110 transition-all duration-300">
                              <FiFileText className="text-white" size={19} />
                            </div>
                            <div className="truncate max-w-[180px] sm:max-w-[240px]">
                              <div className="text-sm font-semibold text-gray-900 group-hover:text-indigo-700 transition-colors duration-200">{contract.name}</div>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-5 align-middle">
                          <span className="text-sm font-medium text-gray-700 group-hover:text-gray-900 transition-colors duration-200">
                            {contract.vendor || <span className="text-gray-400 italic">—</span>}
                          </span>
                        </td>
                        <td className="px-6 py-5 align-middle whitespace-nowrap">
                          <span className="text-sm font-medium text-gray-700 group-hover:text-gray-900 transition-colors duration-200">
                            {contract.value ? formatCurrency(contract.value, contract.currency) : <span className="text-gray-400 italic font-normal">—</span>}
                          </span>
                        </td>
                        <td className="px-6 py-5 align-middle whitespace-nowrap">
                          <span className="text-sm font-medium text-gray-700 group-hover:text-gray-900 transition-colors duration-200">
                            {formatDate(contract.start_date)}
                          </span>
                        </td>
                        <td className="px-6 py-5 align-middle whitespace-nowrap">
                          <span className="text-sm font-medium text-gray-700 group-hover:text-gray-900 transition-colors duration-200">
                            {formatDate(contract.end_date)}
                          </span>
                        </td>
                        <td className="px-6 py-5 align-middle">
                          <span
                            className={`${badgeClasses} group-hover:shadow-md transition-all duration-300`}
                            style={{
                              backgroundColor: isExpired
                                ? '#fef2f2'
                                : isExpiringSoon
                                  ? '#fff7ed'
                                  : '#f0fdf4',
                              color: isExpired
                                ? '#b91c1c'
                                : isExpiringSoon
                                  ? '#c2410c'
                                  : '#15803d',
                              borderColor: isExpired
                                ? '#fecaca'
                                : isExpiringSoon
                                  ? '#fed7aa'
                                  : '#bbf7d0'
                            }}
                            data-status={isExpired ? 'expired' : isExpiringSoon ? 'expiring' : 'active'}
                            data-days={daysRemaining}
                            data-max-alert={badgeDays}
                          >
                            <FiClock className="mr-1.5" size={13} />
                            {isExpired
                              ? 'Expired'
                              : isExpiringSoon
                                ? 'Expiring Soon'
                                : `${daysRemaining} ${daysRemaining === 1 ? 'day' : 'days'} left`
                            }
                          </span>
                        </td>
                        <td className="px-6 py-5 align-middle text-right whitespace-nowrap">
                          <div className="flex items-center justify-end gap-2.5">
                            {(contract.type || '').trim() === 'Policy' && (
                              <button
                                onClick={() => openVersionsModal(contract)}
                                className="p-2.5 text-gray-600 hover:text-white hover:bg-gradient-to-br hover:from-purple-500 hover:to-indigo-600 rounded-xl focus:outline-none transition-all duration-300 border border-gray-200 hover:border-purple-500 shadow-sm hover:shadow-lg hover:scale-110 group-hover:border-purple-300"
                                title="Version history"
                              >
                                <FiLayers size={18} />
                              </button>
                            )}
                            <button
                              onClick={() => handleEditContract(contract)}
                              className="p-2.5 text-gray-600 hover:text-white hover:bg-gradient-to-br hover:from-blue-500 hover:to-blue-600 rounded-xl focus:outline-none transition-all duration-300 border border-gray-200 hover:border-blue-500 shadow-sm hover:shadow-lg hover:scale-110 group-hover:border-blue-300"
                              title="Edit contract"
                            >
                              <FiEdit2 size={18} />
                            </button>
                            {canDeleteContracts && (
                              <button
                                onClick={() => handleDeleteClick(contract)}
                                className="p-2.5 text-gray-600 hover:text-white hover:bg-gradient-to-br hover:from-red-500 hover:to-red-600 rounded-xl focus:outline-none transition-all duration-300 border border-gray-200 hover:border-red-500 shadow-sm hover:shadow-lg hover:scale-110 group-hover:border-red-300"
                                title="Delete contract"
                              >
                                <FiTrash2 size={18} />
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            ) : (
              <div className="px-6 py-20 text-center bg-gradient-to-b from-gray-50 to-white rounded-b-2xl">
                <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-gradient-to-br from-indigo-100 to-blue-100 mb-6 shadow-lg">
                  <FiFileText className="h-10 w-10 text-indigo-500" />
                </div>
                <p className="text-gray-700 text-base font-semibold mb-2">No contracts found</p>
                <p className="text-gray-500 text-sm">Add your first contract to get started</p>
              </div>
            )}
          </div>

          {/* Pagination Controls */}
          {filteredContracts.length > 0 && (
            <div className="px-4 sm:px-6 py-4 border-t border-gray-200 bg-gray-50">
              <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
                <div className="flex flex-col sm:flex-row items-start sm:items-center gap-2 sm:gap-4 w-full sm:w-auto">
                  <span className="text-xs sm:text-sm text-gray-700 whitespace-nowrap">
                    Showing {startIndex + 1} to {Math.min(endIndex, filteredContracts.length)} of {filteredContracts.length}
                  </span>
                  <select
                    value={itemsPerPage}
                    onChange={(e) => {
                      setItemsPerPage(Number(e.target.value));
                      setCurrentPage(1);
                    }}
                    className="w-full sm:w-auto border border-gray-300 rounded-md px-2 py-1.5 text-xs sm:text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value={5}>5 per page</option>
                    <option value={10}>10 per page</option>
                    <option value={20}>20 per page</option>
                    <option value={50}>50 per page</option>
                    <option value={100}>100 per page</option>
                  </select>
                </div>
                <div className="flex items-center justify-center space-x-1 sm:space-x-2 w-full sm:w-auto">
                  <button
                    onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                    disabled={currentPage === 1}
                    className="px-2 sm:px-3 py-1.5 text-xs sm:text-sm border border-gray-300 rounded-md text-gray-700 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    Prev
                  </button>
                  <div className="flex items-center space-x-1">
                    {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                      let pageNum;
                      if (totalPages <= 5) {
                        pageNum = i + 1;
                      } else if (currentPage <= 3) {
                        pageNum = i + 1;
                      } else if (currentPage >= totalPages - 2) {
                        pageNum = totalPages - 4 + i;
                      } else {
                        pageNum = currentPage - 2 + i;
                      }
                      return (
                        <button
                          key={pageNum}
                          onClick={() => setCurrentPage(pageNum)}
                          className={`px-2 sm:px-3 py-1.5 text-xs sm:text-sm border rounded-md transition-colors ${currentPage === pageNum
                            ? 'bg-blue-600 text-white border-blue-600'
                            : 'border-gray-300 text-gray-700 hover:bg-gray-100'
                            }`}
                        >
                          {pageNum}
                        </button>
                      );
                    })}
                  </div>
                  <button
                    onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                    disabled={currentPage === totalPages}
                    className="px-2 sm:px-3 py-1.5 text-xs sm:text-sm border border-gray-300 rounded-md text-gray-700 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    Next
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </main>

      {/* Floating AI Agent - toggles small panel (hidden when SHOW_AGENT is false) */}
      {SHOW_AGENT && (isAgentPanelOpen ? (
        <AgentPanel onClose={() => setIsAgentPanelOpen(false)} />
      ) : (
        <button
          type="button"
          onClick={() => setIsAgentPanelOpen(true)}
          className="fixed bottom-6 right-6 z-40 w-14 h-14 rounded-full bg-indigo-600 text-white shadow-lg hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 flex items-center justify-center"
          title="AI Agent"
          aria-label="Open AI Agent"
        >
          <FiMessageCircle className="w-7 h-7" />
        </button>
      ))}

      {/* Add/Edit Contract Modal — key resets internal state when switching rows or add vs edit */}
      <AddContractModal
        key={
          !isAddModalOpen
            ? 'contract-modal-closed'
            : editingContract?.id != null
              ? `contract-edit-${editingContract.id}`
              : 'contract-add'
        }
        isOpen={isAddModalOpen}
        contracts={contracts}
        onClose={() => {
          setIsAddModalOpen(false);
          setEditingContract(null);
        }}
        onSave={async (formData) => {
          try {
            await handleAddContract(formData, !!editingContract);
            setIsAddModalOpen(false);
            setEditingContract(null);
          } catch (error) {
            console.error('Error saving contract:', error);
            throw error;
          }
        }}
        contract={editingContract}
        user={user}
        departments={departments}
      />

      {/* Delete Confirmation Modal */}
      {isDeleteModalOpen && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Delete Contract</h3>
            <p className="text-gray-600 mb-6">
              Are you sure you want to delete the contract "{contractToDelete?.name}"? This action cannot be undone.
            </p>
            <div className="flex justify-end space-x-3">
              <button
                type="button"
                onClick={() => {
                  setIsDeleteModalOpen(false);
                  setContractToDelete(null);
                }}
                className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleDeleteContract}
                className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Policy Version History Modal */}
      {versionsModalContract && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-lg w-full max-w-2xl max-h-[90vh] flex flex-col overflow-hidden">
            <div className="flex justify-between items-center px-6 py-4 border-b border-gray-200 bg-gray-50">
              <h2 className="text-xl font-semibold text-gray-800 flex items-center">
                <FiLayers className="mr-2 text-purple-600" /> Version history: {versionsModalContract.name}
              </h2>
              <button
                onClick={() => {
                  setVersionsModalContract(null);
                  setPolicyVersions([]);
                  setSelectedVersionSnapshot(null);
                  setVersionsError('');
                }}
                className="text-gray-400 hover:text-red-500 transition-colors"
              >
                <FiX size={26} />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto px-6 py-4">
              {versionsError && <div className="bg-red-100 text-red-700 p-2 rounded mb-3">{versionsError}</div>}
              {versionsLoading && !selectedVersionSnapshot ? (
                <div className="text-gray-500 py-4">Loading versions…</div>
              ) : (
                <>
                  <div className="mb-4">
                    <p className="text-sm text-gray-600 mb-2">Select a version to view or restore:</p>
                    <ul className="space-y-1.5">
                      {policyVersions.map((v) => {
                        const versionFileName = (versionsModalContract?.name || 'policy').replace(/[^a-zA-Z0-9-_]/g, '_') + '_v' + v.version_number + (v.file_url && v.file_url.includes('.') ? v.file_url.slice(v.file_url.lastIndexOf('.')) : '');
                        return (
                          <li key={v.id} className="flex items-center justify-between gap-2 border border-gray-200 rounded-lg px-3 py-2 hover:bg-gray-50">
                            <div className="flex items-center gap-2">
                              <span className="font-medium text-gray-800">Version {v.version_number}</span>
                              <span className="text-xs text-gray-500">
                                {v.created_at ? new Date(v.created_at).toLocaleString() : ''}
                                {v.created_by_username ? ` by ${v.created_by_username}` : ''}
                              </span>
                            </div>
                            <div className="flex flex-wrap items-center gap-1.5">
                              <button
                                type="button"
                                onClick={() => viewVersionSnapshot(v.version_number)}
                                className="px-2 py-1 text-sm text-indigo-600 hover:bg-indigo-50 rounded"
                              >
                                View
                              </button>
                              {v.file_url && (
                                <>
                                  <button
                                    type="button"
                                    onClick={() => viewFile(v.file_url)}
                                    className="px-2 py-1 text-sm text-blue-600 hover:bg-blue-50 rounded"
                                    title="Open file in new tab"
                                  >
                                    View file
                                  </button>
                                  <button
                                    type="button"
                                    onClick={() => downloadFile(v.file_url, versionFileName)}
                                    className="px-2 py-1 text-sm text-gray-700 hover:bg-gray-100 rounded"
                                    title="Download this version"
                                  >
                                    Download
                                  </button>
                                </>
                              )}
                              <button
                                type="button"
                                onClick={() => handleRestoreVersion(v.version_number)}
                                disabled={restoreLoading}
                                className="px-2 py-1 text-sm text-green-600 hover:bg-green-50 rounded disabled:opacity-50"
                              >
                                {restoreLoading ? 'Restoring…' : 'Restore'}
                              </button>
                            </div>
                          </li>
                        );
                      })}
                    </ul>
                    {policyVersions.length === 0 && !versionsLoading && (
                      <p className="text-gray-500 text-sm">No version history yet. Versions are created when you edit this policy.</p>
                    )}
                  </div>
                  {selectedVersionSnapshot && (
                    <div className="mt-4 pt-4 border-t border-gray-200">
                      <p className="text-sm font-medium text-gray-700 mb-2">Snapshot (Version {selectedVersionSnapshot.version_number}):</p>
                      <div className="bg-gray-50 rounded-lg p-4 text-sm space-y-1.5">
                        <p><span className="text-gray-500">Name:</span> {selectedVersionSnapshot.name}</p>
                        <p><span className="text-gray-500">Vendor:</span> {selectedVersionSnapshot.vendor}</p>
                        {selectedVersionSnapshot.description && <p><span className="text-gray-500">Description:</span> {selectedVersionSnapshot.description}</p>}
                        <p><span className="text-gray-500">Status:</span> {selectedVersionSnapshot.status}</p>
                        {selectedVersionSnapshot.file_url && (
                          <div className="flex gap-2 mt-2 pt-2 border-t border-gray-200">
                            <button
                              type="button"
                              onClick={() => viewFile(selectedVersionSnapshot.file_url)}
                              className="px-3 py-1.5 text-sm text-blue-600 hover:bg-blue-50 rounded border border-blue-200"
                            >
                              View file
                            </button>
                            <button
                              type="button"
                              onClick={() => {
                                const base = (versionsModalContract?.name || 'policy').replace(/[^a-zA-Z0-9-_]/g, '_');
                                const ext = selectedVersionSnapshot.file_url.includes('.')
                                  ? selectedVersionSnapshot.file_url.slice(selectedVersionSnapshot.file_url.lastIndexOf('.'))
                                  : '';
                                downloadFile(selectedVersionSnapshot.file_url, base + '_v' + selectedVersionSnapshot.version_number + ext);
                              }}
                              className="px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-100 rounded border border-gray-300"
                            >
                              Download file
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Users Modal */}
      {isUsersModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-lg p-0 w-full max-w-2xl max-h-[90vh] flex flex-col overflow-hidden">
            {/* Modal Header */}
            <div className="flex justify-between items-center px-6 py-4 border-b border-gray-200 bg-gray-50">
              <h2 className="text-2xl font-semibold text-gray-800 flex items-center">
                <FiUsers className="mr-2 text-indigo-600" /> Users
              </h2>
              <button onClick={() => setIsUsersModalOpen(false)} className="text-gray-400 hover:text-red-500 transition-colors">
                <FiX size={28} />
              </button>
            </div>
            {/* Modal Body */}
            <div className="flex-1 overflow-y-auto px-6 py-4">
              {usersError && <div className="bg-red-100 text-red-700 p-2 rounded mb-2">{usersError}</div>}
              <div className="flex justify-between items-center mb-4">
                <span className="text-lg font-medium text-gray-700">User List</span>
                <div className="flex space-x-2">
                  <button
                    onClick={async () => {
                      const result = await refreshPermissions();
                      if (result.success) {
                        alert('Permissions refreshed successfully!');
                        // Reload the users to reflect any changes
                        fetchUsers();
                      } else {
                        alert('Failed to refresh permissions: ' + result.error);
                      }
                    }}
                    className="inline-flex items-center px-3 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors shadow-sm text-sm"
                    title="Refresh your permissions"
                  >
                    <FiKey className="mr-1" /> Refresh Permissions
                  </button>
                  {user && user.permissions && user.permissions.includes('users:create') && (
                    <button
                      onClick={() => setIsCreateUserModalOpen(true)}
                      className="inline-flex items-center px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 transition-colors shadow-sm"
                    >
                      <FiPlus className="mr-2" /> Create User
                    </button>
                  )}
                </div>
              </div>
              {loadingUsers ? (
                <div className="py-8 text-center text-gray-500">Loading users...</div>
              ) : users.length === 0 ? (
                <div className="py-8 text-center text-gray-400">
                  <FiUsers className="mx-auto mb-2 text-4xl opacity-30" />
                  <div>No users found.</div>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="min-w-full text-sm border border-gray-200 rounded-lg overflow-hidden">
                    <thead className="bg-gray-100">
                      <tr>
                        <th className="text-left p-3 font-semibold text-gray-700">Username</th>
                        <th className="text-left p-3 font-semibold text-gray-700">Email</th>
                        <th className="text-left p-3 font-semibold text-gray-700">Role</th>
                        <th className="text-left p-3 font-semibold text-gray-700">Department</th>
                        <th className="text-left p-3 font-semibold text-gray-700">Region</th>
                        <th className="p-3"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {users.map((user, idx) => (
                        <tr key={user.id} className={idx % 2 === 0 ? 'bg-white' : 'bg-gray-50 hover:bg-indigo-50 transition-colors'}>
                          <td className="p-3 text-gray-800">{user.username}</td>
                          <td className="p-3 text-gray-700">{user.email}</td>
                          <td className="p-3 text-gray-700">{user.role_name || user.role || ''}</td>
                          <td className="p-3 text-gray-700">{user.department_name || '—'}</td>
                          <td className="p-3 text-gray-700">{user.region_name || '—'}</td>
                          <td className="p-3 text-right">
                            <button
                              className="inline-flex items-center px-2 py-1 text-sm text-indigo-700 hover:text-indigo-900 hover:bg-indigo-100 rounded transition-colors mr-2"
                              title="Edit User"
                              onClick={() => openEditUserModal(user)}
                            >
                              <FiEdit2 />
                            </button>
                            <button
                              className="inline-flex items-center px-2 py-1 text-sm text-red-700 hover:text-red-900 hover:bg-red-100 rounded transition-colors"
                              title="Delete User"
                              onClick={() => setDeleteUserId(user.id)}
                            >
                              <FiTrash2 />
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
          {/* Create User Modal */}
          {isCreateUserModalOpen && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
              <div className="bg-white rounded-xl shadow-lg p-0 w-full max-w-md flex flex-col overflow-hidden">
                <div className="flex justify-between items-center px-6 py-4 border-b border-gray-200 bg-gray-50">
                  <h2 className="text-xl font-semibold text-gray-800 flex items-center">
                    <FiPlus className="mr-2 text-green-600" /> Create New User
                  </h2>
                  <button onClick={() => setIsCreateUserModalOpen(false)} className="text-gray-400 hover:text-red-500 transition-colors">
                    <FiX size={26} />
                  </button>
                </div>
                <form
                  onSubmit={handleCreateUser}
                  className="space-y-4 px-6 py-4"
                >
                  {userModalError && (
                    <div className="bg-red-100 text-red-700 p-2 rounded mb-2">{userModalError}</div>
                  )}
                  <input
                    type="text"
                    placeholder="Username"
                    className="w-full border rounded px-3 py-2"
                    value={userForm.username}
                    onChange={e => setUserForm(f => ({ ...f, username: e.target.value }))}
                    required
                  />
                  <input
                    type="email"
                    placeholder="Email"
                    className="w-full border rounded px-3 py-2"
                    value={userForm.email}
                    onChange={e => setUserForm(f => ({ ...f, email: e.target.value }))}
                    required
                  />
                  <input
                    type="password"
                    placeholder="Password (optional - leave blank for Microsoft-only login)"
                    className="w-full border rounded px-3 py-2"
                    value={userForm.password}
                    onChange={e => setUserForm(f => ({ ...f, password: e.target.value }))}
                  />
                  <select
                    className="w-full border rounded px-3 py-2"
                    value={userForm.roleId}
                    onChange={e => {
                      const newRoleId = e.target.value;
                      const selectedRole = userRoles.find(r => String(r.id) === String(newRoleId));
                      const scopedDeptIds = selectedRole?.department_ids || [];
                      const scopedRegionIds = selectedRole?.region_ids || [];

                      setUserForm(f => {
                        const next = { ...f, roleId: newRoleId };

                        // If role has scoped departments, auto-pick the first one (unless user already picked one)
                        if (scopedDeptIds.length > 0) {
                          const currentDept = next.departmentId ? String(next.departmentId) : '';
                          const isCurrentAllowed = currentDept && scopedDeptIds.map(String).includes(currentDept);
                          if (!isCurrentAllowed) {
                            next.departmentId = String(scopedDeptIds[0]);
                          }
                        }

                        // If role has scoped regions and user is superadmin (can set region), auto-pick first allowed region
                        if (scopedRegionIds.length > 0) {
                          const currentRegion = next.regionId ? String(next.regionId) : '';
                          const isCurrentAllowed = currentRegion && scopedRegionIds.map(String).includes(currentRegion);
                          if (!isCurrentAllowed) {
                            next.regionId = String(scopedRegionIds[0]);
                          }
                        }

                        return next;
                      });
                    }}
                    required
                  >
                    <option value="">Select Role</option>
                    {userRoles.map(role => (
                      <option key={role.id} value={role.id}>{role.name}</option>
                    ))}
                  </select>
                  {/* Department selector - only visible for admin/superadmin.
                      Note: this is the user's primary department_id; access can still be wider via role scope. */}
                  {(user && (user.role === 'admin' || user.role_name === 'admin' || user.role === 'superadmin' || user.role_name === 'superadmin')) && (
                    <>
                      <select
                        className="w-full border rounded px-3 py-2"
                        value={userForm.departmentId}
                        onChange={e => setUserForm(f => ({ ...f, departmentId: e.target.value }))}
                        required
                      >
                        <option value="">Select Department</option>
                        {(() => {
                          const selectedRole = userRoles.find(r => String(r.id) === String(userForm.roleId));
                          const scopedDeptIds = selectedRole?.department_ids || [];
                          const deptOptions = scopedDeptIds.length > 0
                            ? departments.filter(d => scopedDeptIds.includes(d.id))
                            : departments;
                          return deptOptions.map(dept => (
                            <option key={dept.id} value={dept.id}>{dept.name}</option>
                          ));
                        })()}
                      </select>
                      {(() => {
                        const selectedRole = userRoles.find(r => String(r.id) === String(userForm.roleId));
                        const scopedDeptIds = selectedRole?.department_ids || [];
                        if (scopedDeptIds.length > 0) {
                          return (
                            <div className="text-xs text-gray-500 -mt-2">
                              This role has department scope set. Primary department is limited to the role’s allowed departments.
                            </div>
                          );
                        }
                        return null;
                      })()}
                    </>
                  )}
                  {/* Region selector - only visible for superadmin */}
                  {user && (user.role === 'superadmin' || user.role_name === 'superadmin') && (
                    <select
                      className="w-full border rounded px-3 py-2"
                      value={userForm.regionId}
                      onChange={e => setUserForm(f => ({ ...f, regionId: e.target.value }))}
                    >
                      <option value="">Select Region</option>
                      {(() => {
                        const selectedRole = userRoles.find(r => String(r.id) === String(userForm.roleId));
                        const scopedRegionIds = selectedRole?.region_ids || [];
                        const regionOptions = scopedRegionIds.length > 0
                          ? regions.filter(r => scopedRegionIds.includes(r.id))
                          : regions;
                        return regionOptions.map(region => (
                          <option key={region.id} value={region.id}>{region.name}</option>
                        ));
                      })()}
                    </select>
                  )}
                  <div className="flex justify-end space-x-2 mt-4">
                    <button
                      type="button"
                      onClick={() => setIsCreateUserModalOpen(false)}
                      className="px-4 py-2 border rounded text-gray-700 hover:bg-gray-100"
                      disabled={userModalLoading}
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
                      disabled={userModalLoading}
                    >
                      {userModalLoading ? 'Creating...' : 'Create'}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Roles & Permissions Modal */}
      {isRolesModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-lg w-full max-w-2xl max-h-[90vh] flex flex-col overflow-hidden">
            <div className="flex justify-between items-center px-6 py-4 border-b border-gray-200 bg-gray-50">
              <h2 className="text-2xl font-semibold text-gray-800 flex items-center">
                <FiKey className="mr-2 text-purple-600" /> Roles & Permissions
              </h2>
              <button onClick={() => setIsRolesModalOpen(false)} className="text-gray-400 hover:text-red-500 transition-colors">
                <FiX size={28} />
              </button>
            </div>
            <div className="flex border-b border-gray-200 bg-gray-50">
              <button
                className={`flex-1 px-4 py-2 text-lg font-medium ${rolesTab === 'roles' ? 'text-purple-700 border-b-2 border-purple-600 bg-white' : 'text-gray-500 hover:text-purple-700'}`}
                onClick={() => setRolesTab('roles')}
              >
                Roles
              </button>
              <button
                className={`flex-1 px-4 py-2 text-lg font-medium ${rolesTab === 'permissions' ? 'text-purple-700 border-b-2 border-purple-600 bg-white' : 'text-gray-500 hover:text-purple-700'}`}
                onClick={() => setRolesTab('permissions')}
              >
                Permissions
              </button>
            </div>
            <div className="flex-1 overflow-y-auto px-6 py-8 bg-white">
              {rolesTab === 'roles' ? (
                <div>
                  <div className="flex justify-between items-center mb-4">
                    <span className="text-lg font-medium text-gray-700">Roles</span>
                    <button
                      onClick={() => setIsCreateRoleModalOpen(true)}
                      className="inline-flex items-center px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 transition-colors shadow-sm"
                    >
                      <FiPlus className="mr-2" /> Create Role
                    </button>
                  </div>
                  {loadingRoles ? (
                    <div className="py-8 text-center text-gray-500">Loading roles...</div>
                  ) : rolesError ? (
                    <div className="py-8 text-center text-red-500">{rolesError}</div>
                  ) : roles.length === 0 ? (
                    <div className="py-8 text-center text-gray-400">
                      <FiKey className="mx-auto mb-2 text-4xl opacity-30" />
                      <div>No roles found.</div>
                    </div>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="min-w-full text-sm border border-gray-200 rounded-lg overflow-hidden">
                        <thead className="bg-gray-100">
                          <tr>
                            <th className="text-left p-3 font-semibold text-gray-700">Role Name</th>
                            <th className="text-left p-3 font-semibold text-gray-700">Description</th>
                            <th className="text-left p-3 font-semibold text-gray-700">Permissions</th>
                            <th className="p-3"></th>
                          </tr>
                        </thead>
                        <tbody>
                          {roles.map((role, idx) => (
                            <tr key={role.id} className={idx % 2 === 0 ? 'bg-white' : 'bg-gray-50 hover:bg-purple-50 transition-colors'}>
                              <td className="p-3 text-gray-800 font-medium">{role.name}</td>
                              <td className="p-3 text-gray-700">{role.description}</td>
                              <td className="p-3 text-gray-700">
                                {role.permissions && role.permissions.length > 0 ? (
                                  <div className="flex flex-wrap gap-2">
                                    {role.permissions.map((perm) => (
                                      <span key={perm} className="inline-block bg-purple-100 text-purple-700 px-2 py-1 rounded text-xs font-semibold">{perm}</span>
                                    ))}
                                  </div>
                                ) : (
                                  <span className="text-gray-400">No permissions</span>
                                )}
                              </td>
                              <td className="p-3 text-right">
                                {user && user.permissions && user.permissions.includes('users:update') && (
                                  <button
                                    className="inline-flex items-center px-2 py-1 text-sm text-purple-700 hover:text-purple-900 hover:bg-purple-100 rounded transition-colors"
                                    title="Edit Role"
                                    onClick={() => openEditRoleModal(role)}
                                  >
                                    <FiEdit2 />
                                  </button>
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              ) : (
                <div>
                  <span className="text-lg font-medium text-gray-700 mb-4 block">Permissions</span>
                  {loadingPermissions ? (
                    <div className="py-8 text-center text-gray-500">Loading permissions...</div>
                  ) : permissionsError ? (
                    <div className="py-8 text-center text-red-500">{permissionsError}</div>
                  ) : permissions.length === 0 ? (
                    <div className="py-8 text-center text-gray-400">
                      <FiKey className="mx-auto mb-2 text-4xl opacity-30" />
                      <div>No permissions found.</div>
                    </div>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="min-w-full text-sm border border-gray-200 rounded-lg overflow-hidden">
                        <thead className="bg-gray-100">
                          <tr>
                            <th className="text-left p-3 font-semibold text-gray-700">Permission</th>
                            <th className="text-left p-3 font-semibold text-gray-700">Description</th>
                          </tr>
                        </thead>
                        <tbody>
                          {permissions.map((perm, idx) => (
                            <tr key={perm.name} className={idx % 2 === 0 ? 'bg-white' : 'bg-gray-50 hover:bg-purple-50 transition-colors'}>
                              <td className="p-3 text-gray-800 font-medium">{perm.name}</td>
                              <td className="p-3 text-gray-700">{perm.description || <span className='text-gray-400'>No description</span>}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
          {/* Create/Edit Role Modal */}
          {isCreateRoleModalOpen && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-2 sm:p-4">
              <div className="bg-white rounded-xl shadow-lg p-0 w-full max-w-md flex flex-col overflow-hidden max-h-[90vh]">
                <div className="flex justify-between items-center px-6 py-4 border-b border-gray-200 bg-gray-50 sticky top-0 z-10">
                  <h2 className="text-xl font-semibold text-gray-800 flex items-center">
                    {editingRole ? <><FiEdit2 className="mr-2 text-purple-600" /> Edit Role</> : <><FiPlus className="mr-2 text-green-600" /> Create New Role</>}
                  </h2>
                  <button onClick={() => { setIsCreateRoleModalOpen(false); setEditingRole(null); }} className="text-gray-400 hover:text-red-500 transition-colors">
                    <FiX size={26} />
                  </button>
                </div>
                <form onSubmit={handleCreateRole} className="space-y-4 px-6 py-4 overflow-y-auto">
                  {createRoleError && (
                    <div className="bg-red-100 text-red-700 p-2 rounded mb-2">{createRoleError}</div>
                  )}
                  <input
                    type="text"
                    placeholder="Role Name"
                    className="w-full border rounded px-3 py-2 focus:ring-2 focus:ring-purple-500"
                    value={createRoleForm.name}
                    onChange={e => setCreateRoleForm(f => ({ ...f, name: e.target.value }))}
                    required
                    disabled={!!editingRole}
                  />
                  <input
                    type="text"
                    placeholder="Description"
                    className="w-full border rounded px-3 py-2 focus:ring-2 focus:ring-purple-500"
                    value={createRoleForm.description}
                    onChange={e => setCreateRoleForm(f => ({ ...f, description: e.target.value }))}
                  />
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Permissions</label>
                    <div className="flex flex-wrap gap-2">
                      {permissions.map(perm => (
                        <label key={perm.name} className="inline-flex items-center space-x-2 bg-gray-50 px-2 py-1 rounded cursor-pointer">
                          <input
                            type="checkbox"
                            className="form-checkbox text-purple-600"
                            checked={createRoleForm.permissions.includes(perm.name)}
                            onChange={e => {
                              setCreateRoleForm(f => e.target.checked
                                ? { ...f, permissions: [...f.permissions, perm.name] }
                                : { ...f, permissions: f.permissions.filter(p => p !== perm.name) }
                              );
                            }}
                          />
                          <span className="text-sm text-gray-700">{perm.name}</span>
                        </label>
                      ))}
                    </div>
                  </div>

                  {/* Role scope: Regions */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Allowed Regions (Role Scope)</label>
                    <select
                      multiple
                      className="w-full border rounded px-3 py-2 focus:ring-2 focus:ring-purple-500 bg-white"
                      value={createRoleForm.regionIds.map(String)}
                      onChange={(e) => {
                        const selected = Array.from(e.target.selectedOptions).map(o => parseInt(o.value, 10));
                        setCreateRoleForm(f => ({ ...f, regionIds: selected }));
                      }}
                    >
                      {regions.map(reg => (
                        <option key={reg.id} value={reg.id}>{reg.name}</option>
                      ))}
                    </select>
                    <div className="text-xs text-gray-500 mt-1">Hold Ctrl (Windows) to select multiple.</div>
                  </div>

                  {/* Role scope: Departments */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Allowed Departments (Role Scope)</label>
                    <select
                      multiple
                      className="w-full border rounded px-3 py-2 focus:ring-2 focus:ring-purple-500 bg-white"
                      value={createRoleForm.departmentIds.map(String)}
                      onChange={(e) => {
                        const selected = Array.from(e.target.selectedOptions).map(o => parseInt(o.value, 10));
                        setCreateRoleForm(f => ({ ...f, departmentIds: selected }));
                      }}
                    >
                      {departments.map(dept => (
                        <option key={dept.id} value={dept.id}>{dept.name}</option>
                      ))}
                    </select>
                    <div className="text-xs text-gray-500 mt-1">Hold Ctrl (Windows) to select multiple.</div>
                  </div>
                  <div className="flex justify-end space-x-2 mt-4">
                    <button
                      type="button"
                      onClick={() => { setIsCreateRoleModalOpen(false); setEditingRole(null); }}
                      className="px-4 py-2 border rounded text-gray-700 hover:bg-gray-100"
                      disabled={createRoleLoading}
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className="px-4 py-2 bg-purple-600 text-white rounded hover:bg-purple-700"
                      disabled={createRoleLoading}
                    >
                      {createRoleLoading ? (editingRole ? 'Saving...' : 'Creating...') : (editingRole ? 'Save' : 'Create')}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Edit User Modal */}
      {isEditUserModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-lg p-0 w-full max-w-md flex flex-col overflow-hidden">
            <div className="flex justify-between items-center px-6 py-4 border-b border-gray-200 bg-gray-50">
              <h2 className="text-xl font-semibold text-gray-800 flex items-center">
                <FiEdit2 className="mr-2 text-indigo-600" /> Edit User
              </h2>
              <button onClick={() => { setIsEditUserModalOpen(false); setEditingUser(null); }} className="text-gray-400 hover:text-red-500 transition-colors">
                <FiX size={26} />
              </button>
            </div>
            <form onSubmit={handleEditUser} className="space-y-4 px-6 py-4">
              {editUserError && (
                <div className="bg-red-100 text-red-700 p-2 rounded mb-2">{editUserError}</div>
              )}
              <input
                type="text"
                placeholder="Username"
                className="w-full border rounded px-3 py-2"
                value={editUserForm.username}
                onChange={e => setEditUserForm(f => ({ ...f, username: e.target.value }))}
                required
              />
              <input
                type="email"
                placeholder="Email"
                className="w-full border rounded px-3 py-2"
                value={editUserForm.email}
                onChange={e => setEditUserForm(f => ({ ...f, email: e.target.value }))}
                required
              />
              <input
                type="password"
                placeholder="Password (leave blank to keep current)"
                className="w-full border rounded px-3 py-2"
                value={editUserForm.password}
                onChange={e => setEditUserForm(f => ({ ...f, password: e.target.value }))}
              />
              <select
                className="w-full border rounded px-3 py-2"
                value={editUserForm.roleId}
                onChange={e => setEditUserForm(f => ({ ...f, roleId: e.target.value }))}
                required
              >
                <option value="">Select Role</option>
                {userRoles.map(role => (
                  <option key={role.id} value={role.id}>{role.name}</option>
                ))}
              </select>
              {/* Department selector - only visible for admin/superadmin */}
              {(user && (user.role === 'admin' || user.role_name === 'admin' || user.role === 'superadmin' || user.role_name === 'superadmin')) && (
                <select
                  className="w-full border rounded px-3 py-2"
                  value={editUserForm.departmentId}
                  onChange={e => setEditUserForm(f => ({ ...f, departmentId: e.target.value }))}
                  required
                >
                  <option value="">Select Department</option>
                  {departments.map(dept => (
                    <option key={dept.id} value={dept.id}>{dept.name}</option>
                  ))}
                </select>
              )}
              {/* Region selector - only visible for superadmin */}
              {user && (user.role === 'superadmin' || user.role_name === 'superadmin') && (
                <select
                  className="w-full border rounded px-3 py-2"
                  value={editUserForm.regionId}
                  onChange={e => setEditUserForm(f => ({ ...f, regionId: e.target.value }))}
                >
                  <option value="">Select Region</option>
                  {regions.map(region => (
                    <option key={region.id} value={region.id}>{region.name}</option>
                  ))}
                </select>
              )}
              <div className="flex justify-end space-x-2 mt-4">
                <button
                  type="button"
                  onClick={() => { setIsEditUserModalOpen(false); setEditingUser(null); }}
                  className="px-4 py-2 border rounded text-gray-700 hover:bg-gray-100"
                  disabled={editUserLoading}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-indigo-600 text-white rounded hover:bg-indigo-700"
                  disabled={editUserLoading}
                >
                  {editUserLoading ? 'Saving...' : 'Save'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete User Confirmation Modal */}
      {deleteUserId && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-lg p-0 w-full max-w-sm flex flex-col overflow-hidden">
            <div className="flex justify-between items-center px-6 py-4 border-b border-gray-200 bg-gray-50">
              <h2 className="text-xl font-semibold text-gray-800 flex items-center">
                <FiTrash2 className="mr-2 text-red-600" /> Delete User
              </h2>
              <button onClick={() => setDeleteUserId(null)} className="text-gray-400 hover:text-red-500 transition-colors">
                <FiX size={26} />
              </button>
            </div>
            <div className="px-6 py-6 text-center">
              <p className="mb-4 text-gray-700">Are you sure you want to delete this user?</p>
              <div className="flex justify-end space-x-2">
                <button
                  type="button"
                  onClick={() => setDeleteUserId(null)}
                  className="px-4 py-2 border rounded text-gray-700 hover:bg-gray-100"
                  disabled={deleteUserLoading}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleDeleteUser}
                  className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
                  disabled={deleteUserLoading}
                >
                  {deleteUserLoading ? 'Deleting...' : 'Delete'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Import Modal */}
      <ImportModal
        isOpen={isImportModalOpen}
        onClose={() => setIsImportModalOpen(false)}
        onImportSuccess={handleImportSuccess}
      />

      {/* Change Password Modal */}
      {isChangePasswordModalOpen && user && (
        <ChangePasswordModal
          isOpen={isChangePasswordModalOpen}
          onClose={() => setIsChangePasswordModalOpen(false)}
          userId={user.id}
        />
      )}
    </div>
  );
};

export default Dashboard;
